import { Component, signal, effect, inject, Signal, computed } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, interval, of, map, switchMap, tap } from 'rxjs';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagCreateRequest, IdTagEditRequest, IdTagService } from 'src/app/shared/services/id-tag.service';
import { IdTag } from '../id-tag-model';
import { Department } from '../../department/department.model';
import { Organization } from '../../organization/organization-models';
import { SnackbarService } from 'ok-angular-shared/components';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { Guid } from 'guid-typescript';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';

interface IdTagCreateOrEditModalComponentForm {
  organizationId: FormControl<number | null>;
  selectedDepartmentId: FormControl<number | null>;
  attention: FormControl<string | null>;
  description: FormControl<string | null>;
  shippingAddress: FormControl<string | null>;
  altShippingAddress: FormControl<string | null>;
  externalReference: FormControl<string | null>;
  darId: FormControl<Guid | null>;
  showAltDeliveryAddress: FormControl<boolean>;
  agreementNumber: FormControl<number | null>;
}

export interface IdTagCreateOrEditModalComponentResult {
  action: 'close' | 'process';
  idTag?: IdTag;
}

export interface IdTagCreateOrEditModalData {
  idTagId: number | null;
}

@Component({
  selector: 'adm-id-tag-create-or-edit-modal',
  templateUrl: './id-tag-create-or-edit-modal.component.html',
  styleUrls: ['./id-tag-create-or-edit-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatSlideToggleModule,
    AdmOrgTypeaheadComponent,
    AdmDepTypeaheadComponent,
    DepAddressTypeaheadComponent,
    AdmG4AgreementNumberComponent,
  ],
})
export class IdTagCreateOrEditModalComponent {
  dialogRef = inject<MatDialogRef<IdTagCreateOrEditModalComponent>>(MatDialogRef);
  data = inject<IdTagCreateOrEditModalData>(MAT_DIALOG_DATA);
  resetOrgTrigger = signal<{ reset: boolean }>({ reset: false });
  resetDepartmentFilterSignal = signal<{ reset: boolean }>({ reset: false });
  private idTagService = inject(IdTagService);
  private snackbarService = inject(SnackbarService);
  private idTagIdSubject = new BehaviorSubject<number | null>(this.data?.idTagId);

  isEdit = signal<boolean>((this.data?.idTagId ?? 0) > 0);
  saving = signal(false);
  isLimited = signal(false);
  IdTagTypeEnum = IdTagTypeEnum;

  initialAltAddress = signal<InitialAddress | null>(null);

  public step1FormGroup: FormGroup<IdTagCreateOrEditModalComponentForm> = new FormGroup<IdTagCreateOrEditModalComponentForm>({
    organizationId: new FormControl<number | null>(null),
    selectedDepartmentId: new FormControl<number | null>(null, [Validators.required]),
    attention: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null, [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]),
    shippingAddress: new FormControl<string | null>({ value: null, disabled: true }),
    altShippingAddress: new FormControl<string | null>(null),
    externalReference: new FormControl<string | null>(null),
    darId: new FormControl<Guid | null>(null),
    showAltDeliveryAddress: new FormControl<boolean>(false, { nonNullable: true }),
    agreementNumber: new FormControl<number | null>(null),
  });

  constructor() {
    effect(() => {
      const limited = this.isLimited();
      if (limited) {
        this.step1FormGroup.controls.attention.disable();
        this.step1FormGroup.controls.showAltDeliveryAddress.disable();
      } else {
        this.step1FormGroup.controls.attention.enable();
        this.step1FormGroup.controls.showAltDeliveryAddress.enable();
      }
    });
  }

  idTag: Signal<IdTag | null> = toSignal(
    this.idTagIdSubject.pipe(
      distinctUntilChanged(),
      switchMap(idTagId => {
        if (idTagId) {
          return this.idTagService.getIdTag(idTagId);
        }
        return of(null);
      }),
      tap(idTag => {
        if (idTag) {
          this.step1FormGroup.patchValue({
            organizationId: idTag?.organizationId,
            selectedDepartmentId: this.getDepartmentIdForIdTag(idTag),
            description: idTag?.description,
            attention: idTag?.attention,
            externalReference: idTag?.externalReference,
            agreementNumber: idTag?.paymentInfo,
          });

          this.initialAltAddress.set({ fullAddress: idTag.shippingAddress, darId: idTag.darId ? Guid.parse(idTag.darId) : undefined });

          if (!this.isFleetIdTag(idTag)) {
            this.step1FormGroup.controls.selectedDepartmentId.clearValidators();
            this.step1FormGroup.controls.agreementNumber.disable();
          }

          this.isLimited.set(this.isEdit() && idTag.status.toString() !== IdTagStatusEnum[IdTagStatusEnum.Pending]);
          if (this.isLimited()) {
            this.step1FormGroup.controls.attention.disable();
          }
        }
      }),
      map(idTag => idTag),
      takeUntilDestroyed()
    ),
    { initialValue: null }
  );

  initialDepartmentId = computed(() => {
    const idTag = this.idTag();
    if (!idTag) return undefined;
    return this.getDepartmentIdForIdTag(idTag)?.toString();
  });

  public isFleetIdTag(idTag: IdTag | null | undefined): boolean {
    if (!idTag) {
      return false;
    }

    return idTag.idTagType?.toString() === IdTagTypeEnum[IdTagTypeEnum.FleetIdTag];
  }

  private getDepartmentIdForIdTag(idTag: IdTag): number | undefined {
    return this.isFleetIdTag(idTag) ? idTag.departmentId : idTag.chargingUserDepartmentId;
  }

  departmentFetched(departments: Department[] | null) {
    if (this.isEdit()) {
      if (!departments) {
        return;
      }

      const shippingAddress = this.idTag()?.shippingAddress;
      const idTagAddressIsDepartmentAddress =
        departments.map(d => d.address).length > 0 && shippingAddress
          ? departments.map(d => d.address).indexOf(shippingAddress) > -1
          : false;
      this.step1FormGroup.patchValue({
        showAltDeliveryAddress: idTagAddressIsDepartmentAddress ? false : true,
      });
    }
  }

  altAddressToggled = toSignal(
    this.step1FormGroup.controls.showAltDeliveryAddress.valueChanges.pipe(
      tap(val => {
        if (val === true) {
          if (!this.isEdit()) {
            this.step1FormGroup.patchValue({
              altShippingAddress: '',
            });
          }
          this.step1FormGroup.controls.altShippingAddress.setValidators(Validators.required);
        } else {
          this.step1FormGroup.patchValue({
            altShippingAddress: '',
          });
          this.step1FormGroup.controls.altShippingAddress.clearValidators();
        }
      })
    )
  );

  public async getDepartments(organization: Organization | null) {
    this.step1FormGroup.patchValue({
      organizationId: organization?.id ? organization.id : null,
    });
    if (!this.isEdit()) {
      this.resetDepartmentFilterSignal.set({ reset: true });
    }
    if (organization && !organization.idTagGroupName) {
      this.snackbarService.error(
        'Der er ikke tilknyttet en id tag gruppe til organisationen. Derfor kan man ikke oprette brikker til denne organisation'
      );
      this.step1FormGroup.controls.selectedDepartmentId.disable();
      return;
    }
    this.step1FormGroup.controls.selectedDepartmentId.enable();
  }

  public resetOrganization() {
    this.step1FormGroup.patchValue({
      organizationId: null,
      selectedDepartmentId: null,
      shippingAddress: null,
      agreementNumber: null,
    });
    this.resetDepartmentFilterSignal.set({ reset: true });

    this.step1FormGroup.controls.selectedDepartmentId.disable();
  }

  public resetDepartment() {
    this.step1FormGroup.patchValue({
      selectedDepartmentId: null,
      shippingAddress: null,
      agreementNumber: null,
    });
  }

  public selectDepartment(department: Department | null) {
    if (department) {
      if (this.isEdit()) {
        const idTag = this.idTag();
        if (!idTag) {
          return;
        }
        this.step1FormGroup.patchValue({
          darId: idTag.darId ? Guid.parse(idTag.darId) : null,
          selectedDepartmentId: department.id,
          shippingAddress: department.address,
          altShippingAddress: idTag.shippingAddress,
          agreementNumber: idTag?.paymentInfo ?? department?.agreementNumber ?? null,
        });

        return;
      }
      this.step1FormGroup.patchValue({
        shippingAddress: department.address,
        selectedDepartmentId: department.id,
        darId: department.darId,
        altShippingAddress: null,
        showAltDeliveryAddress: department.darId ? false : true,
        agreementNumber: department?.agreementNumber ?? null,
      });

      const showAltDeliveryAddressCtrl = this.step1FormGroup.controls.showAltDeliveryAddress;

      if (department.darId) {
        showAltDeliveryAddressCtrl.enable();
      } else {
        showAltDeliveryAddressCtrl.disable();
      }
    }
  }

  public selectAltAddress(address: AddressChanged | null) {
    if (address && address.fullAddress !== null) {
      this.step1FormGroup.patchValue({
        altShippingAddress: address.formatAddressToString,
        darId: address.darId ? address.darId : null,
      });
    }
  }

  public externalReferenceBlur() {
    if (this.step1FormGroup.controls.externalReference.value === '') {
      this.step1FormGroup.controls.externalReference.setValue(null);
    }
  }

  public close() {
    this.dialogRef.close(<IdTagCreateOrEditModalComponentResult>{
      action: 'close',
    });
  }

  public async saveIdTagAndContinue() {
    if (this.step1FormGroup.invalid) {
      return;
    }
    const formValue = this.step1FormGroup.getRawValue();
    this.saving.set(true);
    if (!formValue.darId) {
      this.saving.set(false);
      return;
    }

    const idTag = {
      departmentId: formValue.selectedDepartmentId,
      darId: formValue.darId.toString(),
      shippingAddress:
        formValue.altShippingAddress && formValue.altShippingAddress != '' ? formValue.altShippingAddress : formValue.shippingAddress,
      externalReference: formValue.externalReference,
      description: formValue.description,
      attention: formValue.attention,
      idToken: null,
      printedId: null,
      agreementNumber: formValue.agreementNumber,
    } as IdTagCreateRequest;

    const idTagId = await firstValueFrom(this.idTagService.createIdTag(idTag));
    const idTagRes = await firstValueFrom(this.idTagService.getIdTag(idTagId));
    this.saving.set(false);
    this.dialogRef.close(<IdTagCreateOrEditModalComponentResult>{
      action: 'process',
      idTag: idTagRes,
    });
  }

  public async saveIdTagAndClose() {
    if (this.step1FormGroup.invalid) {
      return;
    }

    const formValue = this.step1FormGroup.getRawValue();
    this.saving.set(true);
    const idTag = this.idTag();
    if (!idTag) {
      this.saving.set(false);
      return;
    }
    const editIdTag = {
      departmentId: formValue.selectedDepartmentId,
      darId: formValue.darId ? formValue.darId.toString() : null,
      shippingAddress:
        formValue.altShippingAddress && formValue.altShippingAddress != '' ? formValue.altShippingAddress : formValue.shippingAddress,
      externalReference: formValue.externalReference,
      description: formValue.description,
      attention: formValue.attention,
      agreementNumber: formValue.agreementNumber,
    } as IdTagEditRequest;

    this.idTagService
      .editIdTag(idTag.id, editIdTag)
      .subscribe({
        next: async idTagId => {
          const idTagRes = await firstValueFrom(this.idTagService.getIdTag(idTagId));
          this.saving.set(false);
          this.dialogRef.close(<IdTagCreateOrEditModalComponentResult>{
            action: 'close',
            idTag: idTagRes,
          });
        },
      })
      .add(() => {
        interval(700).subscribe(() => {
          this.saving.set(false);
        });
      });
  }
}
